import { format, isAfter, isBefore } from 'date-fns';
import { convertUTCToTimezone, sortBy } from '@betgames/bg-tools';
import { SortDirection } from '@bg-shared/enums';
import { sessionStore } from '@bg-shared/business/Session';
import {
    IGamificationAchievedBadge,
    IGamificationBadge,
    IGamificationBadgeGame,
} from '../interfaces';
import {
    ICUAchievedBadge,
    ICUBadge,
    ICUBadgeProgress,
    ICUCondition,
    ICUProfileActivity,
} from '../services/CaptainUpAPI';
import {
    GamificationActivityType,
    GamificationConditionOperator,
    GamificationConditionFilter,
} from '../enums';

class BadgesFormatter {
    private extractGames(badge: ICUBadge, progress: ICUBadgeProgress): IGamificationBadgeGame[] {
        const games: IGamificationBadgeGame[] =
            badge.conditions?.reduce((result, condition) => {
                condition.filters
                    ?.filter((filter) => filter.field === GamificationConditionFilter.GAMES_IDS)
                    .forEach((filter) => {
                        filter.values[0].forEach((value) => {
                            result.push({
                                gameId: parseInt(value, 10),
                                completed: progress?.conditions[condition.id].completed || false,
                            });
                        });
                    });

                return result;
            }, []) ?? [];

        return games.reduce((result, game) => {
            const index = result.findIndex((exGame) => game.gameId === exGame.gameId);

            if (index === -1) {
                result.push(game);
            } else if (result[index].completed && !game.completed) {
                result[index] = game;
            }

            return result;
        }, []);
    }

    private getConditionsStepsAmount(
        conditions: ICUCondition[],
        conditionsOperator: GamificationConditionOperator,
    ): number {
        return conditions.reduce((prev: number, condition: ICUCondition) => {
            if (conditionsOperator === GamificationConditionOperator.OR) {
                return Math.max(prev, condition.consecutive * condition.times);
            }

            return prev + condition.consecutive * condition.times;
        }, 0);
    }

    public formatBadge(badge: ICUBadge, progress: ICUBadgeProgress): IGamificationBadge {
        return {
            id: badge.id,
            active: badge.active,
            currencies: {
                points: badge.currencies.points,
                coins: badge.currencies.coins,
            },
            name: badge.name,
            description: badge.description,
            descriptionHtml: badge.description_html,
            image: badge.preset_image,
            title: badge.title,
            activeFrom: badge.active_from,
            activeFromDate: badge.active_from_date,
            startTime: badge.active_from ? Date.parse(badge.active_from_date) : null,
            disabledFrom: badge.disabled_from,
            disabledFromDate: badge.disabled_from_date,
            endTime: badge.disabled_from ? Date.parse(badge.disabled_from_date) : null,
            completed: progress?.completed ?? false,
            progress: progress?.progress ?? 0,
            createdTime: Date.parse(badge.created_at),
            games: this.extractGames(badge, progress),
            conditionsStepsAmount: this.getConditionsStepsAmount(
                badge.conditions,
                badge.conditions_operator,
            ),
        };
    }

    public formatBadges([badges, badgesProgress]: [
        ICUBadge[],
        Record<string, ICUBadgeProgress>,
    ]): Record<string, IGamificationBadge> {
        return badges.reduce<Record<string, IGamificationBadge>>((acc, badge) => {
            if (badge.is_segmented_for_user && badge.visible) {
                acc[badge.id] = this.formatBadge(badge, badgesProgress[badge.id]);
            }
            return acc;
        }, {});
    }

    public formatChallenges(
        badges: Record<string, IGamificationBadge>,
        timezone: number,
        stateBadges: IGamificationBadge[],
        lastSeenBadges: string[],
    ): IGamificationBadge[] {
        return Object.values(badges)
            .filter((badge) => {
                if (!badge.active || badge.completed) {
                    return false;
                }

                let isActiveBadge = true;

                if (badge.activeFrom) {
                    isActiveBadge = isAfter(
                        convertUTCToTimezone(Date.now(), timezone),
                        badge.startTime,
                    );
                }

                if (isActiveBadge && badge.disabledFrom) {
                    isActiveBadge = isBefore(
                        convertUTCToTimezone(Date.now(), timezone),
                        badge.endTime,
                    );
                }

                return isActiveBadge;
            })
            .map((badge) => {
                badge.unread = stateBadges
                    ? stateBadges.find((stateBadge) => stateBadge.id === badge.id)?.unread
                    : !lastSeenBadges?.find((id) => id === badge.id);

                return badge;
            })
            .sort(
                sortBy<IGamificationBadge>(
                    [
                        (item) => item.endTime || Infinity,
                        (item) => item.progress,
                        (item) => item.currencies.points,
                    ],
                    [SortDirection.ASC, SortDirection.DESC, SortDirection.ASC],
                ),
            );
    }

    public updateAchievedChallenges(
        challenges: IGamificationBadge[],
        ids: string[],
    ): [IGamificationBadge[], IGamificationAchievedBadge[]] {
        const impactedChallenges = ids
            .map((id) => challenges.find((challenge) => challenge.id === id))
            .filter(Boolean)
            .map((challenge) => ({
                ...challenge,
                unread: true,
                unseen: true,
                timesCompleted: 1,
                achievementsDates: null,
                date: this.formatAchievedChallengesDate(Date.now(), sessionStore.timezone),
            }));

        const impactedIds = impactedChallenges.map((challenge) => challenge.id);

        const updatedChallenges = challenges.filter(
            (challenge) => !impactedIds.includes(challenge.id),
        );

        return [updatedChallenges, impactedChallenges];
    }

    public formatAchievedChallengesDate(time: number, timezone: number): string {
        return format(convertUTCToTimezone(time || Date.now(), timezone), sessionStore.dateFormat);
    }

    public formatAchievedChallenges(
        achievedBadges: ICUAchievedBadge[],
        badges: Record<string, IGamificationBadge>,
        unreadActivities: Record<string, ICUProfileActivity>,
        timezone: number,
    ): IGamificationAchievedBadge[] {
        return achievedBadges
            .filter((achievedBadge) => !!badges[achievedBadge.id])
            .sort(
                (currentItem, nextItem) =>
                    Date.parse(nextItem.achievements_dates.last) -
                    Date.parse(currentItem.achievements_dates.last),
            )
            .map((achievedBadge) => ({
                ...badges[achievedBadge.id],
                id: achievedBadge.id,
                timesCompleted: achievedBadge.times_completed,
                achievementsDates: achievedBadge.achievements_dates,
                unread: !!unreadActivities[achievedBadge.id],
                unseen: !!unreadActivities[achievedBadge.id],
                date: this.formatAchievedChallengesDate(
                    Date.parse(achievedBadge.achievements_dates?.last),
                    timezone,
                ),
            }));
    }

    public formatActivities(activities: ICUProfileActivity[]): Record<string, ICUProfileActivity> {
        return activities.reduce<Record<string, ICUProfileActivity>>((acc, activity) => {
            if (activity.entity.type === GamificationActivityType.BADGE) {
                acc[activity.entity.id] = activity;
            }

            return acc;
        }, {});
    }
}

export const badgesFormatter = new BadgesFormatter();
